import { useFormikContext } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1 } from '@ui/Typography'
import { Link } from '@ui/components/Link'

import { validateIsTrue } from '@utils/formValidation'

import { LegalDocumentDto, isLegalDocumentWidgetCheckbox } from '@model/CompanyLegalDocumentDto'

import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'

import { renderCommaSeparated } from './DocumentReadAndAgreeField'

export interface DocumentFieldItem {
  documents: LegalDocumentDto[]
  component: React.ReactNode
}

interface FormValues {
  documents: { [key: string]: boolean }
  agreedLegalDocuments: AgreedLegalDocument[]
}

const FormCheckBoxField = createFormCheckBoxField()

interface DocumentGroupFieldProps {
  groupReference: string
  documents: LegalDocumentDto[]
  renderItem?(props: DocumentFieldItem): React.ReactNode
}

interface AgreedLegalDocument {
  id: string
  openedOn: Date
  agreedOn: Date
}

export const DocumentCheckBoxGroupField: React.FC<DocumentGroupFieldProps> = (props) => {
  const { groupReference, documents, renderItem } = props

  const { values, setValues } = useFormikContext<FormValues>()

  const handleChange = (value: boolean) => {
    if (groupReference) {
      const agreedLegalDocumentsRest = values.agreedLegalDocuments.filter(
        (x) => !documents.find((doc) => x.id === doc.id)
      )

      const agreedLegalDocuments = documents
        .filter(isLegalDocumentWidgetCheckbox)
        .map(createAgreedLegalCheckBoxDocument)
        .concat(agreedLegalDocumentsRest)
        .filter(filterAgreedLegalDocumentsDuplicates)

      setValues({
        ...values,
        documents: {
          ...values.documents,
          [groupReference]: value,
        },
        agreedLegalDocuments: agreedLegalDocuments,
      })
    }
  }

  return (
    <CheckBoxGroupField
      documents={documents}
      groupReference={groupReference}
      renderItem={renderItem}
      onChange={handleChange}
    />
  )
}

export interface CheckBoxGroupFieldProps {
  groupReference: string
  documents: LegalDocumentDto[]
  onChange(value: boolean): void
  renderItem?(props: DocumentFieldItem): React.ReactNode
}

const CheckBoxGroupField: React.FC<CheckBoxGroupFieldProps> = (props) => {
  const { groupReference, documents, onChange, renderItem } = props

  const { t } = useTranslation()

  const defaultComponent = (
    <React.Fragment>
      <Body1>{t('Sign up.I confirm that I have read, consent and agree to the')}</Body1>{' '}
      {renderCommaSeparated(documents.map((x) => renderDocumentLink(x)))}{' '}
      <span className='text'>*</span>
    </React.Fragment>
  )

  return (
    <FormCheckBoxField
      name={`documents.${groupReference}`}
      validate={validateIsTrue(t('Validation.Required'))}
      onChange={onChange}
    >
      <React.Fragment>
        {renderItem
          ? renderItem({
              documents,
              component: defaultComponent,
            })
          : defaultComponent}
      </React.Fragment>
    </FormCheckBoxField>
  )
}

interface DocumentCheckBoxFieldFactoryProps {
  documents: LegalDocumentDto[]
  renderItem?(props: DocumentFieldItem): React.ReactNode
}

export const DocumentCheckBoxField: React.FC<DocumentCheckBoxFieldFactoryProps> = (props) => {
  const { documents = [], renderItem } = props
  const [document] = documents

  const { values, setValues } = useFormikContext<FormValues>()

  const handleChange = (value: boolean) => {
    const agreedLegalDocuments = values.agreedLegalDocuments
      .filter(filterAgreedLegalDocumentsByDocument(document))
      .concat(createAgreedLegalCheckBoxDocument(document))
      .filter(filterAgreedLegalDocumentsDuplicates)

    setValues({
      ...values,
      documents: {
        ...values.documents,
        [document.id]: value,
      },
      agreedLegalDocuments,
    })
  }

  return <CheckBoxField documents={[document]} onChange={handleChange} renderItem={renderItem} />
}

export interface CheckBoxFieldProps {
  documents: LegalDocumentDto[]
  onChange(value: boolean): void
  renderItem?(props: DocumentFieldItem): React.ReactNode
}

const CheckBoxField: React.FC<CheckBoxFieldProps> = (props) => {
  const { documents = [], onChange, renderItem } = props
  const [document] = documents

  const { t } = useTranslation()

  const defaultComponent = (
    <React.Fragment>
      {document?.url && <Body1>{t('I agree with')} </Body1>}
      {renderDocumentLink(document)} <Body1>*</Body1>
    </React.Fragment>
  )

  return (
    <FormCheckBoxField
      name={`documents.${document.id}`}
      validate={validateIsTrue(t('Validation.Required'))}
      onChange={onChange}
      className='text'
    >
      <React.Fragment>
        {renderItem
          ? renderItem({
              documents: [document],
              component: defaultComponent,
            })
          : defaultComponent}
      </React.Fragment>
    </FormCheckBoxField>
  )
}

const renderDocumentLink = (document: LegalDocumentDto) => {
  const { url, title } = document

  if (!url) {
    return (
      <span className='text' key={document.id}>
        {title}
      </span>
    )
  }

  return (
    <Link key={document.id} size={'lg'} title={title || undefined} href={url}>
      {title}
    </Link>
  )
}

const filterAgreedLegalDocumentsByDocument =
  (document: LegalDocumentDto) => (x: AgreedLegalDocument) => {
    return x.id !== document.id
  }

const createAgreedLegalCheckBoxDocument = (document: LegalDocumentDto) => {
  return {
    id: document.id,
    openedOn: new Date(),
    agreedOn: new Date(),
  }
}

const filterAgreedLegalDocumentsDuplicates = (
  value: AgreedLegalDocument,
  index: number,
  self: AgreedLegalDocument[]
) => {
  return index === self.findIndex((t) => t.id === value.id && t.id === value.id)
}
